import get from 'lodash/get';

import { bugsnagError } from 'services/bugsnag';
import warehouse from 'services/warehouse';

import { Design, Product, Render, Revision, Space } from 'redux/models';
import { UserFavoriteActions, UserFavoriteSelectors } from 'services/Data';

import {
  STUDIO_ADD_OR_UPDATE_DESIGN,
  STUDIO_ADD_OR_UPDATE_DESIGN_SNAPSHOT,
  STUDIO_ADD_OR_UPDATE_PACKAGES,
  STUDIO_ADD_OR_UPDATE_PRODUCT,
  STUDIO_ADD_OR_UPDATE_REVISION,
  STUDIO_ADD_OR_UPDATE_RENDERS,
  STUDIO_ADD_OR_UPDATE_SPACE,
  STUDIO_BATCH_ADD_OR_UPDATE_PRODUCTS,
  STUDIO_DELETE_DESIGN,
  STUDIO_RECEIVE_PRODUCTS_PURCHASED,
  SELECT_HOTSPOT_PRODUCT,
  SELECT_PDP_HOTSPOT_PRODUCT,
  STUDIO_SET_ROUTER_PARAMS,
  STUDIO_SET_SHOW_HIDE_LOOKBOOK,
  STUDIO_SET_PRODUCT_VIEW_ACTIVE,
  STUDIO_SET_DESIGN_SELECTOR_ACTIVE,
  STUDIO_SET_LAYOUT_MODAL_ACTIVE,
  STUDIO_SET_LOCALLY_SELECTED_LAYOUT,
  STUDIO_TOGGLE_BANNER_Z_INDEX
} from './action-constants';

import { getSpaceFor, getDesignFor, getIndexedProducts, getProductFor } from './selectors';

/**
 * updateAllUserFavorites
 */
export const updateAllUserFavorites = () => async (dispatch, getState) => {
  try {
    // Get all the liked product ids
    await dispatch(UserFavoriteActions.fetchAllUserFavorites());
    const allLikedProductIds = UserFavoriteSelectors.getAllLikedProductIds(getState());

    // Get all the products from state
    const productsInState = getIndexedProducts(getState());

    // Populate array with all ids of liked products which are not in state
    let likedProductIdsNotInState = [];
    likedProductIdsNotInState = allLikedProductIds
      .filter(productId => !productsInState[productId]);

    // Loads the missing liked products into state
    const whProducts = await warehouse.getProductsByIds(likedProductIdsNotInState);
    dispatch(studioProductsFromWarehouseProductLoaded({ whProducts }));
  } catch (error) {
    bugsnagError(error);
  }
};

/** Sets the router params state. */
export const syncRouterParams = (routerParams) => ({
  type: STUDIO_SET_ROUTER_PARAMS,
  payload: routerParams
});

/**
 * Add space to application state.
 * Overwrites space if already existing.
 */
export const addOrUpdateSpace = space => ({
  type: STUDIO_ADD_OR_UPDATE_SPACE,
  payload: space
});

// actions / thunks
export const studioSpaceListLoaded = ({ whSpaceList }) => (dispatch, getState) => {
  whSpaceList.forEach(whSpace => {
    // If we already have the space in state, never overwrite.
    // This prevents a full space from being overwritten by a space brief.
    const existingSpace = getSpaceFor({ spaceId: whSpace.id, state: getState() });
    if (!existingSpace) {
      // Construct our internal space representation.
      const space = Space.createSpaceFromWarehouseResponse({ whSpace });

      // Add space to state
      dispatch(addOrUpdateSpace(space));
    }
  });
};

export const studioSpaceLoaded = ({ whSpace }) => dispatch => {
  // Ignore archived designs.
  whSpace.designs = whSpace.designs.filter(whDesign => !whDesign.is_archived);

  // Construct our internal space representation.
  const space = Space.createSpaceFromWarehouseResponse({ whSpace });

  // Add space to state
  dispatch(addOrUpdateSpace(space));

  // Construct our internal design representations
  // and add them to state. These will be design briefs.
  // Ignore pending designs for now.
  whSpace.designs.forEach(whDesign => {
    dispatch(studioDesignLoaded({ spaceId: space.id(), whDesign, showroomSpace: space.isShowroomSpace() }));
  });
};

export const studioDesignLoaded = ({ spaceId, whDesign, showroomSpace }) => (dispatch, getState) => {
  // If the device does not support webGL filter out the 360 renders
  //
  // For user created designs, filter out 360 renders until we add
  // full support for both Bessie and LiveSwap revisions.
  const hasWebGL = getState().device.hasWebGL;
  const createdByUser = [
    Design.Source.LIVE_SWAP,
    Design.Source.BESSIE_REDESIGN,
    Design.Source.SHOWROOM
  ].includes(whDesign.source);

  if (!hasWebGL || createdByUser) {
    whDesign.renders = whDesign.renders.filter(render => !render.view_360);
  }

  // Only keep active designs.
  let design = Design.createDesignFromWarehouseResponse({ spaceId, whDesign, showroomSpace });
  if (!design.canBeViewedByUser()) { return; }

  // If the design has revisions, then
  // create revisions and render objects and add to state.
  // This should happen before adding the design to state to prevent any flicker.
  const whRevisionHistory = get(whDesign, 'designsnapshot_history', []);
  if (whRevisionHistory) {
    whRevisionHistory.forEach(whDesignSnapshot => {
      dispatch(studioDesignsnapshotLoaded({ whDesignSnapshot }));
    });
  }

  // Add the design object to state.
  return dispatch(studioAddOrUpdateDesign(design));
};

/** Adds a designsnapshot to state. */
export const studioDesignsnapshotLoaded = ({ whDesignSnapshot }) => (dispatch, getState) => {
  // Make sure replacement products are downloaded and added to state.
  const replaceInfo = whDesignSnapshot.replace_info;
  if (replaceInfo) {
    const replaceProductIds = Object.values(replaceInfo).map(productInfo => productInfo.product_id);
    replaceProductIds.forEach(productId => {
      dispatch(loadStudioProduct({ productId }));
    });
  }

  // Handle renders.
  const whRenders = whDesignSnapshot.renderings || {};
  let renders = [];
  Object.keys(whRenders).forEach(cameraName => {
    const whRender = whRenders[cameraName];
    renders.push(Render.createRenderFromWarehouseRendering({
      cameraName,
      revisionUuid: whDesignSnapshot.id,
      whRender
    }));
  });
  dispatch(studioAddOrUpdateRenders(renders));

  // Handle revision.
  const revision = Revision.createRevisionFromWarehouseDesignSnapshot({ whDesignSnapshot });
  dispatch(studioAddOrUpdateRevision(revision));
};

/**
 * Add revision to application state.
 * Overwrites revision if already existing.
 */
export const studioAddOrUpdateRevision = revision => ({
  type: STUDIO_ADD_OR_UPDATE_REVISION,
  payload: revision
});

/**
 * Add renders to application state.
 * Overwrites individual renders if already existing.
 */
export const studioAddOrUpdateRenders = renders => ({
  type: STUDIO_ADD_OR_UPDATE_RENDERS,
  payload: renders
});

/**
* Loads batch of products to state
* @param  {Array} whProducts StyleboardProduct instances which includes product instance + additional metadata.
* There is a known issue where Modsy Styling Props are being returned in the results
* These items can randomly cause errors.
* filterModsyStylingProps: filters them out before they are rendered into the Studio view
*/
export const studioProductsLoaded = ({ whProducts }) => dispatch => {
  const filterModsyStylingProps = whProducts.filter(function (el) {
    return el.product.source !== 'Modsy Styling Props';
  });

  const transformedProducts = filterModsyStylingProps
    .map(whProduct => {
      if (!whProduct.product) { return null; } // Handle bad server response.
      return Product.createProductFromWarehouseResponse({ whProduct });
    })
    .filter(prod => {
      return prod !== null;
    });

  // Batch add products to state
  dispatch({
    type: STUDIO_BATCH_ADD_OR_UPDATE_PRODUCTS,
    payload: transformedProducts
  });
};

/**
* Loads batch of products to state
* @param  {Array} whProducts pure Product instances from server
*/
export const studioProductsFromWarehouseProductLoaded = ({ whProducts }) => dispatch => {
  const products = whProducts
    .map(whProduct => Product.createProductFromWarehouseProductResponse({ whProduct }));

  // Batch add products to state
  dispatch({
    type: STUDIO_BATCH_ADD_OR_UPDATE_PRODUCTS,
    payload: products
  });
};

/**
 * Loads a product from server if the product
 * is not already in state.
 */
export const loadStudioProduct = ({ productId }) => async (dispatch, getState) => {
  if (getProductFor({ productId, state: getState() })) {
    return; // product already loaded
  }

  try {
    const whProduct = await warehouse.getProductById(productId);
    const product = Product.createProductFromWarehouseProductResponse({ whProduct });
    dispatch(addStudioProduct(product));
  } catch (error) {
    bugsnagError(error, { productId });
  }
};

export const addStudioProducts = products => dispatch => {
  dispatch({
    type: STUDIO_BATCH_ADD_OR_UPDATE_PRODUCTS,
    payload: products
  });
};

export const addStudioProduct = product => dispatch => {
  dispatch({
    type: STUDIO_BATCH_ADD_OR_UPDATE_PRODUCTS,
    payload: [product]
  });
};

export const requestProductsPurchased = () => (dispatch) => {
  return warehouse.getProductsPurchased().then(response => {
    dispatch({
      type: STUDIO_RECEIVE_PRODUCTS_PURCHASED,
      payload: response.products
    });
  });
};

export const requestStudioSpaceList = ({ pageSize, userId }) => dispatch => {
  return warehouse.getStudioSpaceListFor({ pageSize, userId })
    .then(response => {
      let whSpaceList = get(response, 'results', []);

      // Filter out abandoned designs.
      whSpaceList.forEach(whSpace => {
        whSpace.designs = whSpace.designs.filter(design => {
          return design.status !== Design.Status.ABANDONED;
        });
      });

      dispatch(studioSpaceListLoaded({ whSpaceList }));
    })
    .catch(error => {
      window.bugsnag.notify(error);
    });
};

export const receiveProductsPurchased = productsPurchasedIds => ({
  type: STUDIO_RECEIVE_PRODUCTS_PURCHASED,
  payload: productsPurchasedIds
});

export const studioUpdateProduct = product => ({
  type: STUDIO_ADD_OR_UPDATE_PRODUCT,
  payload: product
});

export const studioAddOrUpdateDesign = design => ({
  type: STUDIO_ADD_OR_UPDATE_DESIGN,
  payload: design
});

export const studioAddOrUpdateDesignSnapshot = designSnapshot => ({
  type: STUDIO_ADD_OR_UPDATE_DESIGN_SNAPSHOT,
  payload: designSnapshot
});

/**
* Abandon the design corresponding to designId
*/
export const studioDeleteDesign = (designToDelete) => dispatch => {
  dispatch({
    type: STUDIO_DELETE_DESIGN,
    payload: designToDelete
  });

  return warehouse.abandonDesign(designToDelete.id())
    .catch(error => {
      bugsnagError(error, { designToDelete });
    });
};

/**
 * Updates the space flag that instructs whether or not user
 * has seen the Welcome To Your Studio Modal yet.
 */
export const welcomeToStudioModalShownFor = space => dispatch => {
  const newSpace = space.makeCopy({ showWelcomeToStudio: false });
  dispatch(addOrUpdateSpace(newSpace));
};

export const setRenderViewed = (renderId, designId) => (dispatch, getState) => {
  const newDesign = getDesignFor({designId, state: getState()}).makeCopy();

  newDesign.render360Viewed(renderId);

  dispatch(studioAddOrUpdateDesign(newDesign));
};

export const selectHotspotProduct = (productId, hotspotUid, fromHotspot = false) => (dispatch, getState) => {
  let state = getState();
  let product = getProductFor({ productId, state });

  dispatch({
    type: SELECT_HOTSPOT_PRODUCT,
    payload: { product, hotspotUid, fromHotspot }
  });
};

export const selectPDPOnlyHotspotProduct = (productId, hotspotUid) => (dispatch, getState) => {
  const state = getState();
  const product = getProductFor({ productId, state });
  dispatch({
    type: SELECT_PDP_HOTSPOT_PRODUCT,
    payload: { product, hotspotUid }
  });
};

/**
 * Sets the show lookbook flag to true.
 */
export const showLookbook = () => ({
  type: STUDIO_SET_SHOW_HIDE_LOOKBOOK,
  payload: true
});

/**
 * Sets the show lookbook flag to false.
 */
export const hideLookbook = () => ({
  type: STUDIO_SET_SHOW_HIDE_LOOKBOOK,
  payload: false
});

/**
 * Shows/hides the left hand overlay design selector
 */
export const setDesignSelectorActive = ({ active }) => ({
  type: STUDIO_SET_DESIGN_SELECTOR_ACTIVE,
  payload: active
});

/**
 * Sets flag to render the product view
 * @param {Boolean} active
 */
export const setProductViewActive = active => ({
  type: STUDIO_SET_PRODUCT_VIEW_ACTIVE,
  payload: active
});

/**
 * Sets packages data in state so we don't need to request it again
 * @param {Array} packagesData
 */
export const setPackageData = packagesData => ({
  type: STUDIO_ADD_OR_UPDATE_PACKAGES,
  payload: packagesData
});

/**
 * Sets whether the layout submission modal should be rendered or not
 */
export const setLayoutModalActive = active => ({
  type: STUDIO_SET_LAYOUT_MODAL_ACTIVE,
  payload: active
});

/**
 * Sets whether the layout submission modal should be rendered or not
 */
export const setLocallySelectedLayoutId = layoutId => ({
  type: STUDIO_SET_LOCALLY_SELECTED_LAYOUT,
  payload: layoutId
});

/**
 * Toggles z-index used in Router > BannerContainer.
 * [on phone only] we want to update BannerContainer z-index based on whether or not
 * the drawer menu or a dropdown is open from our Monarch header. This allows us to keep
 * the scroll up effect that nicely slides design title above the top banners.
 */
export const toggleBannerZindex = showElements => ({
  type: STUDIO_TOGGLE_BANNER_Z_INDEX,
  payload: showElements ? 0 : 1
});
